import type { BucketAggregate } from "../../../schemas/time-series/data-stream-query.js";
import { dayMs, hourMs, yearMs } from "../../../timeConstants.js";
import type { DataVisibility } from "../../portConfigs.js";
import { UnitCategory } from "../../SensorConfigurations/SensorConfigurationTypes.js";
import { Unit } from "../UnitConfigurations.js";

/** Narrowed to a category's allowed units, every unit is required; widened to all units, each is optional. */
type UnitRecord<U extends Unit> = [Unit] extends [U] ? Partial<Record<Unit, number>> : Record<U, number>;

/** For values that never go stale, such as zone geometry. Also the largest `staleAfterMs` allowed (migration 013). */
export const indefiniteMs = yearMs * 50;

export type UnitCategoryConfiguration<U extends Unit = Unit> = {
    allowedUnits: readonly U[];
    databaseUnit: Unit;
    /** A stream silent longer than this is drawn as a gap instead of a line. */
    staleAfterMs: number;

    /**
     * The bucket aggregate that is *meaningful* for series in this category, where that is something
     * other than a plain mean. Advisory: the data-point API still honours whatever a caller asks for,
     * and omitting this reads as "mean", which is right for the instantaneous spot readings that make
     * up most of the catalogue.
     *
     * It belongs to the category because it follows from the phenomenon being measured, not from the
     * device that reported it:
     *
     * - An **accumulation** over the reporting interval (`precipitation`, `evapotranspiration`) wants
     *   `sum`, which is exact and, being invariant to how the period was partitioned, survives a
     *   station changing its reporting interval mid-series.
     * - An **interval extremum** (`windGust`) wants `max`, which is likewise exact: the maximum of a
     *   set of interval maxima is the maximum of the underlying samples. Note this makes the
     *   peak-value question exact and nothing more — distributional queries over such a series
     *   (median, percentile, threshold counts) still scale with the reporting interval.
     * - A **running total or a categorical state** (`electricalPulseCount`, `outputPosition`,
     *   `outputOperatingMode`, `solenoidVerification`) wants `last`, the value as of the end of the
     *   bucket. A cumulative counter's mean is its total at no instant anyone asked about, and an
     *   enum's mean is not a member of the enum. `last` beats `max` for the counter because it reports
     *   a device reset honestly instead of hiding it behind the pre-reset peak.
     *
     * A phenomenon that aggregates unlike its neighbours therefore earns its own category rather than
     * an override further down: `windGust` is split from `windSpeed` for exactly this reason, even
     * though the two share units, a conversion, and an instrument.
     *
     * `dimensionless` is the converse, and stays unset: it holds lifetime counters and opaque codes
     * together, and no one aggregate suits both.
     */
    preferredBucketAggregate?: BucketAggregate;

    /**
     * The mark a graph draws series in this category with, where that is something other than a
     * line. Omitting it reads as `line`, which suits a level held across the interval — the shape of
     * most of the catalogue.
     *
     * `bar` is for a value that is an amount collected over the bucket, where the mark's area is the
     * quantity and the gaps between marks are meaningful. That often coincides with a `sum`
     * aggregate, but the two are separate questions and each category answers them on its own:
     * `windGust` aggregates with `max` and could still be drawn as bars, and a `sum` category read
     * for its trend rather than its per-period amount is better as a line.
     */
    preferredSeriesType?: "bar" | "line";

    /** Only for categories always about the device; shared ones like `electricalVoltage` stay unset. */
    visibility?: DataVisibility;
    displayStep: UnitRecord<U>;

    maxDecimals: UnitRecord<U>;
};

export const UnitCategoryConfigurations = {
    liquidFlow: {
        staleAfterMs: hourMs * 12,
        databaseUnit: "liters_per_minute",
        allowedUnits: ["liters_per_minute", "liters_per_hour", "gallons_per_minute", "gallons_per_hour"],
        displayStep: { liters_per_minute: 1, liters_per_hour: 10, gallons_per_minute: 0.5, gallons_per_hour: 1 },
        maxDecimals: { liters_per_minute: 1, liters_per_hour: 0, gallons_per_minute: 1, gallons_per_hour: 0 },
    },
    humidity: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "percent_rh",
        allowedUnits: ["percent_rh"],
        displayStep: { percent_rh: 1 },
        maxDecimals: { percent_rh: 0 },
    },
    airTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
        displayStep: { celsius: 0.5, fahrenheit: 1 },
        maxDecimals: { celsius: 1, fahrenheit: 1 },
    },
    boardTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
        visibility: "diagnostic",
        displayStep: { celsius: 0.5, fahrenheit: 1 },
        maxDecimals: { celsius: 1, fahrenheit: 1 },
    },
    plantTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
        displayStep: { celsius: 0.5, fahrenheit: 1 },
        maxDecimals: { celsius: 1, fahrenheit: 1 },
    },
    soilTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
        displayStep: { celsius: 0.5, fahrenheit: 1 },
        maxDecimals: { celsius: 1, fahrenheit: 1 },
    },
    liquidPressure: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "psi", "bar"],
        displayStep: { kilopascal: 1, psi: 0.5, bar: 0.05 },
        maxDecimals: { kilopascal: 1, psi: 1, bar: 2 },
    },
    volumetricSoilMoisture: {
        staleAfterMs: hourMs * 12,
        databaseUnit: "percent",
        allowedUnits: ["percent"],
        displayStep: { percent: 1 },
        maxDecimals: { percent: 1 },
    },
    resistiveSoilMoisture: {
        staleAfterMs: hourMs * 12,
        allowedUnits: ["kilopascal"],
        databaseUnit: "kilopascal",
        displayStep: { kilopascal: 1 },
        maxDecimals: { kilopascal: 0 },
    },
    soilTension: {
        staleAfterMs: hourMs * 12,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal"],
        displayStep: { kilopascal: 1 },
        maxDecimals: { kilopascal: 0 },
    },
    soilElectricalConductivity: {
        staleAfterMs: dayMs,
        databaseUnit: "microsiemens_per_cm",
        allowedUnits: ["microsiemens_per_cm", "deciSiemens_per_meter"],
        displayStep: { microsiemens_per_cm: 10, deciSiemens_per_meter: 0.01 },
        maxDecimals: { microsiemens_per_cm: 0, deciSiemens_per_meter: 2 },
    },
    poreSoilElectricalConductivity: {
        staleAfterMs: dayMs,
        databaseUnit: "microsiemens_per_cm",
        allowedUnits: ["microsiemens_per_cm", "deciSiemens_per_meter"],
        displayStep: { microsiemens_per_cm: 10, deciSiemens_per_meter: 0.01 },
        maxDecimals: { microsiemens_per_cm: 0, deciSiemens_per_meter: 2 },
    },
    relativePermittivity: {
        staleAfterMs: dayMs,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        displayStep: { dimensionless: 1 },
        maxDecimals: { dimensionless: 2 },
    },
    windSpeed: {
        staleAfterMs: dayMs,
        databaseUnit: "meters_per_second",
        allowedUnits: ["meters_per_second", "kilometer_per_hour", "miles_per_hour"],
        displayStep: { meters_per_second: 0.5, kilometer_per_hour: 1, miles_per_hour: 1 },
        maxDecimals: { meters_per_second: 1, kilometer_per_hour: 0, miles_per_hour: 0 },
    },
    windGust: {
        staleAfterMs: dayMs,
        databaseUnit: "meters_per_second",
        allowedUnits: ["meters_per_second", "kilometer_per_hour", "miles_per_hour"],
        preferredBucketAggregate: "max",
        displayStep: { meters_per_second: 0.5, kilometer_per_hour: 1, miles_per_hour: 1 },
        maxDecimals: { meters_per_second: 1, kilometer_per_hour: 0, miles_per_hour: 0 },
    },
    windHeading: {
        staleAfterMs: dayMs,
        databaseUnit: "degree",
        allowedUnits: ["degree"],
        displayStep: { degree: 1 },
        maxDecimals: { degree: 0 },
    },
    soilVolumetricIonContent: {
        staleAfterMs: dayMs,
        databaseUnit: "percent_vic",
        allowedUnits: ["percent_vic"],
        displayStep: { percent_vic: 0.1 },
        maxDecimals: { percent_vic: 1 },
    },
    precipitation: {
        staleAfterMs: dayMs,
        databaseUnit: "millimeter",
        allowedUnits: ["millimeter", "inch"],
        preferredBucketAggregate: "sum",
        preferredSeriesType: "bar",
        displayStep: { millimeter: 0.5, inch: 0.01 },
        maxDecimals: { millimeter: 1, inch: 2 },
    },
    liquidTankLevel: {
        staleAfterMs: dayMs,
        // Every liquidTankLevel sensor converts volts to metres of head above the sensor, not a
        // percent-full-of-vessel figure — that would need tank geometry we don't model anywhere yet.
        databaseUnit: "meter",
        allowedUnits: ["meter", "foot"],
        displayStep: { meter: 0.1, foot: 0.5 },
        maxDecimals: { meter: 2, foot: 1 },
    },
    sapFlow: {
        staleAfterMs: dayMs,
        databaseUnit: "liters_per_hour",
        allowedUnits: ["liters_per_hour", "gallons_per_hour"],
        displayStep: { liters_per_hour: 0.1, gallons_per_hour: 0.01 },
        maxDecimals: { liters_per_hour: 2, gallons_per_hour: 2 },
    },
    stemWaterPotential: {
        staleAfterMs: dayMs,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "psi", "bar"],
        displayStep: { kilopascal: 10, psi: 1, bar: 0.1 },
        maxDecimals: { kilopascal: 0, psi: 0, bar: 1 },
    },
    electricalVoltage: {
        staleAfterMs: dayMs,
        databaseUnit: "volt",
        allowedUnits: ["volt"],
        displayStep: { volt: 0.01 },
        maxDecimals: { volt: 2 },
    },
    evapotranspiration: {
        staleAfterMs: dayMs,
        databaseUnit: "millimeter",
        allowedUnits: ["millimeter", "inch"],
        preferredBucketAggregate: "sum",
        displayStep: { millimeter: 0.1, inch: 0.01 },
        maxDecimals: { millimeter: 1, inch: 2 },
    },
    electricalPulseCount: {
        staleAfterMs: dayMs,
        databaseUnit: "pulse",
        allowedUnits: ["pulse"],
        preferredBucketAggregate: "last",
        displayStep: { pulse: 1 },
        maxDecimals: { pulse: 0 },
    },
    flowmeterKFactor: {
        staleAfterMs: dayMs,
        databaseUnit: "liters_per_pulse",
        allowedUnits: ["liters_per_pulse", "gallons_per_pulse"],
        displayStep: { liters_per_pulse: 0.01, gallons_per_pulse: 0.01 },
        maxDecimals: { liters_per_pulse: 3, gallons_per_pulse: 3 },
    },
    flowmeterOffset: {
        staleAfterMs: dayMs,
        databaseUnit: "hertz",
        allowedUnits: ["pulse_per_minute"],
        displayStep: { pulse_per_minute: 1 },
        maxDecimals: { pulse_per_minute: 1 },
    },
    flowmeterMinimumFrequency: {
        staleAfterMs: dayMs,
        databaseUnit: "hertz",
        allowedUnits: ["pulse_per_minute"],
        displayStep: { pulse_per_minute: 1 },
        maxDecimals: { pulse_per_minute: 1 },
    },
    pressureTransducerSlope: {
        staleAfterMs: dayMs,
        databaseUnit: "kilopascal_per_volt",
        allowedUnits: ["kilopascal_per_volt"],
        displayStep: { kilopascal_per_volt: 1 },
        maxDecimals: { kilopascal_per_volt: 2 },
    },
    liquidTankLevelSlope: {
        staleAfterMs: dayMs,
        databaseUnit: "meter_per_volt",
        allowedUnits: ["meter_per_volt"],
        displayStep: { meter_per_volt: 0.01 },
        maxDecimals: { meter_per_volt: 3 },
    },
    dimensionless: {
        staleAfterMs: dayMs,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        displayStep: { dimensionless: 1 },
        maxDecimals: { dimensionless: 2 },
    },
    arbitraryPercentage: {
        staleAfterMs: dayMs,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        displayStep: { dimensionless: 1 },
        maxDecimals: { dimensionless: 1 },
    },
    /** Categorical; see: {@link import("../../SensorConfigurations/outputPosition.js").OutputPosition}. */
    outputPosition: {
        staleAfterMs: dayMs * 7,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        preferredBucketAggregate: "last",
        // Growers read valve state off the zone card's irrigation band, not this 0/1 line.
        visibility: "diagnostic",
        displayStep: { dimensionless: 1 },
        maxDecimals: { dimensionless: 0 },
    },
    /** Categorical; see: {@link import("../../SensorConfigurations/outputOperatingMode.js").OutputOperatingMode}. */
    outputOperatingMode: {
        staleAfterMs: dayMs * 7,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        preferredBucketAggregate: "last",
        visibility: "diagnostic",
        displayStep: { dimensionless: 1 },
        maxDecimals: { dimensionless: 0 },
    },
    /** Categorical; see: {@link import("../../SensorConfigurations/solenoidVerification.js").SolenoidVerificationResult}. */
    solenoidVerification: {
        staleAfterMs: dayMs * 7,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        preferredBucketAggregate: "last",
        visibility: "diagnostic",
        displayStep: { dimensionless: 1 },
        maxDecimals: { dimensionless: 0 },
    },
    ndvi: {
        staleAfterMs: dayMs,
        databaseUnit: "ndvi",
        allowedUnits: ["ndvi"],
        displayStep: { ndvi: 0.01 },
        maxDecimals: { ndvi: 2 },
    },
    ndviVariance: {
        staleAfterMs: dayMs,
        databaseUnit: "ndvi",
        allowedUnits: ["ndvi"],
        displayStep: { ndvi: 0.001 },
        maxDecimals: { ndvi: 3 },
    },
    stemRadiusChange: {
        staleAfterMs: dayMs,
        databaseUnit: "micrometer",
        allowedUnits: ["micrometer"],
        displayStep: { micrometer: 1 },
        maxDecimals: { micrometer: 0 },
    },
    solarRadiation: {
        staleAfterMs: dayMs,
        databaseUnit: "watt_per_square_meter",
        allowedUnits: ["watt_per_square_meter"],
        displayStep: { watt_per_square_meter: 10 },
        maxDecimals: { watt_per_square_meter: 0 },
    },
    depth: {
        staleAfterMs: dayMs,
        databaseUnit: "meter",
        allowedUnits: ["meter", "centimeter", "foot", "inch"],
        displayStep: { meter: 0.01, centimeter: 1, foot: 0.1, inch: 1 },
        maxDecimals: { meter: 2, centimeter: 0, foot: 1, inch: 0 },
    },
    barometricPressure: {
        staleAfterMs: dayMs,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "hectopascal", "inches_of_mercury"],
        displayStep: { kilopascal: 0.1, hectopascal: 1, inches_of_mercury: 0.01 },
        maxDecimals: { kilopascal: 1, hectopascal: 0, inches_of_mercury: 2 },
    },
    uvIndex: {
        staleAfterMs: dayMs,
        databaseUnit: "uv_index",
        allowedUnits: ["uv_index"],
        displayStep: { uv_index: 0.1 },
        maxDecimals: { uv_index: 1 },
    },
    inFieldSpacing: {
        staleAfterMs: indefiniteMs,
        databaseUnit: "meter",
        allowedUnits: ["meter", "centimeter", "foot", "inch"],
        displayStep: { meter: 0.05, centimeter: 1, foot: 0.5, inch: 1 },
        maxDecimals: { meter: 2, centimeter: 0, foot: 1, inch: 0 },
    },
    rowLength: {
        staleAfterMs: indefiniteMs,
        databaseUnit: "meter",
        allowedUnits: ["meter", "foot"],
        displayStep: { meter: 1, foot: 1 },
        maxDecimals: { meter: 0, foot: 0 },
    },
    emitterFlowRate: {
        staleAfterMs: indefiniteMs,
        databaseUnit: "liters_per_hour",
        allowedUnits: ["liters_per_hour", "gallons_per_hour"],
        displayStep: { liters_per_hour: 0.1, gallons_per_hour: 0.05 },
        maxDecimals: { liters_per_hour: 2, gallons_per_hour: 2 },
    },
    totalIrrigationVolume: {
        staleAfterMs: dayMs,
        databaseUnit: "liter",
        allowedUnits: ["liter", "gallon", "acre_foot"],
        displayStep: { liter: 100, gallon: 10, acre_foot: 0.01 },
        maxDecimals: { liter: 0, gallon: 0, acre_foot: 3 },
    },
} as const satisfies Record<UnitCategory, UnitCategoryConfiguration>;

// displayStep and maxDecimals must have an entry for every allowed unit.
UnitCategoryConfigurations satisfies {
    [K in UnitCategory]: UnitCategoryConfiguration<(typeof UnitCategoryConfigurations)[K]["allowedUnits"][number]>;
};

/**
 * Runtime type guard for `unitCategory` values arriving over the wire (typed loosely as `string`).
 * The keys of `UnitCategoryConfigurations` are the authoritative known set. Uses `hasOwnProperty`
 * so inherited prototype keys (e.g. "toString") are not mistaken for known categories.
 */
export function isKnownUnitCategory(value: string): value is UnitCategory {
    return Object.prototype.hasOwnProperty.call(UnitCategoryConfigurations, value);
}

/** Safe accessor: returns the configuration for a known category, or `undefined` for an unknown one. */
export function getUnitCategoryConfiguration(value: string): UnitCategoryConfiguration | undefined {
    return isKnownUnitCategory(value) ? UnitCategoryConfigurations[value] : undefined;
}
